import { lazy } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "next-themes";
import { Analytics } from "@vercel/analytics/react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { AuthProvider } from "@/context/AuthContext";
import { AppLayout, ManagerRoute } from "@/components/layout/AppLayout";
import { PageTransition } from "@/components/PageTransition";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ErrorBoundary } from "@/components/ErrorBoundary";

const LandingPage = lazy(() => import("./pages/LandingPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const VerifyPage = lazy(() => import("./pages/VerifyPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));
const CompanyProfilePage = lazy(() => import("./pages/CompanyProfilePage"));
const SalesOutreachPage = lazy(() => import("./pages/SalesOutreachPage"));
const RecommendationsPage = lazy(() => import("./pages/RecommendationsPage"));
const CompanyAnalysisDashboard = lazy(() => import("./pages/CompanyAnalysisDashboard"));
const AnalyzedCompaniesPage = lazy(() => import("./pages/AnalyzedCompaniesPage"));
const ClientProfilePage = lazy(() => import("./pages/ClientProfilePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));
const SubscriptionSuccessPage = lazy(() => import("./pages/SubscriptionSuccessPage"));
const AuthCallbackPage = lazy(() => import("./pages/AuthCallbackPage"));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));

const queryClient = new QueryClient();

const router = createBrowserRouter([
  {
    path: "*",
    element: (
      <>
        <ScrollToTop />
        <AnimatedRoutes />
      </>
    ),
  },
]);

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        {/* Public pages */}
        <Route
          path="/"
          element={
            <PageTransition>
              <LandingPage />
            </PageTransition>
          }
        />
        <Route
          path="/privacy-policy"
          element={
            <PageTransition>
              <PrivacyPolicyPage />
            </PageTransition>
          }
        />
        <Route
          path="/terms-of-service"
          element={
            <PageTransition>
              <TermsOfServicePage />
            </PageTransition>
          }
        />
        <Route
          path="/cookie-policy"
          element={
            <PageTransition>
              <CookiePolicyPage />
            </PageTransition>
          }
        />

        {/* Auth screens */}
        <Route
          path="/login"
          element={
            <PageTransition>
              <LoginPage />
            </PageTransition>
          }
        />
        <Route
          path="/register"
          element={
            <PageTransition>
              <RegisterPage />
            </PageTransition>
          }
        />
        <Route
          path="/verify"
          element={
            <PageTransition>
              <VerifyPage />
            </PageTransition>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PageTransition>
              <ForgotPasswordPage />
            </PageTransition>
          }
        />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route
          path="/onboarding"
          element={
            <PageTransition>
              <OnboardingPage />
            </PageTransition>
          }
        />

        {/* App screens wrapped in layout */}
        <Route
          path="/company-profile"
          element={
            <PageTransition>
              <AppLayout>
                <CompanyProfilePage />
              </AppLayout>
            </PageTransition>
          }
        />
        <Route
          path="/client-profile"
          element={
            <PageTransition>
              <AppLayout>
                <ClientProfilePage />
              </AppLayout>
            </PageTransition>
          }
        />
        <Route
          path="/sales-outreach"
          element={
            <PageTransition>
              <AppLayout>
                <ManagerRoute>
                  <SalesOutreachPage />
                </ManagerRoute>
              </AppLayout>
            </PageTransition>
          }
        />
        <Route
          path="/recommendations"
          element={
            <PageTransition>
              <AppLayout>
                <ManagerRoute>
                  <RecommendationsPage />
                </ManagerRoute>
              </AppLayout>
            </PageTransition>
          }
        />
        <Route
          path="/analysis/:id"
          element={
            <PageTransition>
              <AppLayout>
                <ManagerRoute>
                  <CompanyAnalysisDashboard />
                </ManagerRoute>
              </AppLayout>
            </PageTransition>
          }
        />
        <Route
          path="/analyzed-companies"
          element={
            <PageTransition>
              <AppLayout>
                <ManagerRoute>
                  <AnalyzedCompaniesPage />
                </ManagerRoute>
              </AppLayout>
            </PageTransition>
          }
        />
        <Route
          path="/subscription"
          element={
            <PageTransition>
              <AppLayout>
                <SubscriptionPage />
              </AppLayout>
            </PageTransition>
          }
        />
        <Route
          path="/subscription/success"
          element={
            <PageTransition>
              <SubscriptionSuccessPage />
            </PageTransition>
          }
        />

        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route
          path="*"
          element={
            <PageTransition>
              <NotFound />
            </PageTransition>
          }
        />
      </Routes>
    </AnimatePresence>
  );
}

const App = () => (
  <HelmetProvider>
    <Helmet>
      <title>Easly — AI Sales Intelligence Platform</title>
    </Helmet>
    <ErrorBoundary>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <TooltipProvider>
              <Sonner />
              <RouterProvider router={router} />
            </TooltipProvider>
          </AuthProvider>
        </QueryClientProvider>
        <Analytics />
      </ThemeProvider>
    </ErrorBoundary>
  </HelmetProvider>
);

export default App;
